import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";
import { WorkoutExerciseType } from "../models/workoutPlanViewModel";

interface ITypeOption {
  type: WorkoutExerciseType;
  label: string;
  sub: string;
}

export const EXERCISE_TYPE_OPTIONS: ITypeOption[] = [
  {
    type: WorkoutExerciseType.KgReps,
    label: "Кг × Повторения",
    sub: "Класически режим — тегло и брой повторения.",
  },
  {
    type: WorkoutExerciseType.Time,
    label: "Време",
    sub: "Само секунди. Подходящо за планк и други подобни упражнения.",
  },
  {
    type: WorkoutExerciseType.Distance,
    label: "Разстояние",
    sub: "Тегло × разстояние в метри или километри. Подходящо за фермерска разходка.",
  },
  {
    type: WorkoutExerciseType.TimePace,
    label: "Време + Темпо",
    sub: "Време и скорост (км/ч), с темпото в мин/км. Подходящо за бягане и кардио.",
  },
];

const OPTIONS = EXERCISE_TYPE_OPTIONS;

interface IExerciseTypePickerProps {
  isOpen: boolean;
  current: WorkoutExerciseType;
  onClose: () => void;
  onPick: (type: WorkoutExerciseType) => void;
  /** Confirmation copy. Surfaced when the chosen type differs from `current`. */
  changeWarning?: string;
}

export const ExerciseTypePicker = ({
  isOpen,
  current,
  onClose,
  onPick,
  changeWarning = "Преминаването към друг режим може да промени текущите стойности на всички сетове и всички предишни тренировки, в които това упражнение е било използвано.",
}: IExerciseTypePickerProps) => {
  // Two-step flow: a destructive switch needs an explicit confirmation. Without
  // it a stray tap could wipe a chunk of data the user spent the last minute typing.
  const [pendingType, setPendingType] = useState<WorkoutExerciseType | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) return;
    setPendingType(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handlePick = (type: WorkoutExerciseType) => {
    if (type === current) {
      onClose();
      return;
    }
    setPendingType(type);
  };

  const confirm = () => {
    if (pendingType === null) return;
    onPick(pendingType);
    setPendingType(null);
    onClose();
  };

  const pendingLabel =
    pendingType !== null
      ? (OPTIONS.find((o) => o.type === pendingType)?.label ?? "")
      : "";

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Тип упражнение"
          >
            <div className={styles.rest_picker_title}>Тип упражнение</div>
            <div className={styles.rest_picker_sub}>
              Избери как се измерват сетовете на това упражнение.
            </div>

            <div className={styles.type_picker_list}>
              {OPTIONS.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  className={classNames(styles.type_picker_item, {
                    [styles.type_picker_item_active]: option.type === current,
                  })}
                  onClick={() => handlePick(option.type)}
                >
                  <div className={styles.type_picker_item_label}>
                    {option.label}
                  </div>
                  <div className={styles.type_picker_item_sub}>
                    {option.sub}
                  </div>
                </button>
              ))}
            </div>

            {pendingType !== null && (
              <motion.div
                className={styles.type_picker_confirm}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                <div className={styles.type_picker_confirm_text}>
                  Превключи към „{pendingLabel}“?
                  <br />
                  <span className={styles.type_picker_confirm_warning}>
                    {changeWarning}
                  </span>
                </div>
                <div className={styles.type_picker_confirm_actions}>
                  <button
                    type="button"
                    className={styles.type_picker_confirm_cancel}
                    onClick={() => setPendingType(null)}
                  >
                    Отказ
                  </button>
                  <button
                    type="button"
                    className={styles.type_picker_confirm_ok}
                    onClick={confirm}
                  >
                    Превключи
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export const exerciseTypeLabel = (type: WorkoutExerciseType): string => {
  const match = OPTIONS.find((o) => o.type === type);
  return match?.label ?? "—";
};
